import type { ComponentPropsWithoutRef } from 'react';
import { useEffect, useRef } from 'react';

import type { ComboBoxStateOptions } from '@react-stately/combobox';

import { useComboBoxState } from '@react-stately/combobox';

import type { AriaComboBoxOptions } from '@react-aria/combobox';

import { useComboBox } from '@react-aria/combobox';

import { Button } from './Button';
import { Popover } from './Popover';
import { ListBox } from './ListBox';

import { Box, Grid, Textfield, LoadingSpinner } from '@simplywallst/ui-core';

import SearchSolid from '@simplywallst/ui-core/icons/SearchSolid';

interface Props<T>
  extends Pick<
      ComboBoxStateOptions<T>,
      | 'items'
      | 'menuTrigger'
      | 'selectedKey'
      | 'placeholder'
      | 'children'
      | 'isDisabled'
      | 'inputValue'
      | 'onInputChange'
      | 'onSelectionChange'
    >,
    Pick<AriaComboBoxOptions<T>, 'placeholder' | 'label' | 'onFocus'> {
  emptyStateRender?: ComponentPropsWithoutRef<
    typeof ListBox
  >['emptyStateRender'];
  onEmptyResults?: (searchInput: string) => void;
  status: 'pending' | 'success' | 'error';
}

export const ComboBox = <T extends object>({
  label,
  items,
  selectedKey,
  menuTrigger = 'focus',
  children,
  inputValue,
  isDisabled,
  placeholder,
  emptyStateRender,
  onEmptyResults,
  onFocus,
  onInputChange,
  onSelectionChange,
  status,
}: Props<T>) => {
  const state = useComboBoxState({
    children,
    items,
    menuTrigger,
    allowsEmptyCollection: typeof emptyStateRender !== 'undefined',
    inputValue,
    selectedKey,
    onInputChange,
    onSelectionChange,
  });

  const buttonRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listBoxRef = useRef<HTMLUListElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const { buttonProps, inputProps, listBoxProps } = useComboBox(
    {
      label,
      onFocus,
      isDisabled,
      placeholder,
      inputRef,
      buttonRef,
      listBoxRef,
      popoverRef,
    },
    state
  );

  const { isOpen, close, collection } = state;

  useEffect(() => {
    if (collection.size === 0) {
      onEmptyResults?.(inputValue || '');
    }
  }, [collection.size, inputValue, onEmptyResults]);

  const isIdleState =
    (state.inputValue.length === 0 || status === 'pending') &&
    collection.size === 0;

  return (
    <Box width="100%" position="relative">
      <Textfield
        {...inputProps}
        type="text"
        ref={inputRef}
        prefix={
          <Button {...buttonProps} ref={buttonRef}>
            <Grid aria-hidden="true" alignItems="center">
              <SearchSolid fill="var(--textfield-addon-color)" />
            </Grid>
          </Button>
        }
        postfix={
          Number(inputValue?.length) && status === 'pending' ? (
            <LoadingSpinner styleType="light" />
          ) : null
        }
      />
      {isOpen && (
        <Popover
          ref={popoverRef}
          state={state}
          triggerRef={inputRef}
          isNonModal
          placement="bottom start"
        >
          {!isIdleState ? (
            <ListBox
              {...listBoxProps}
              close={close}
              ref={listBoxRef}
              state={state}
              emptyStateRender={emptyStateRender}
            />
          ) : null}
        </Popover>
      )}
    </Box>
  );
};
